"use client"

import { useCallback } from "react"

import { useParams, usePathname, useRouter as useBrowserRouter, useSearchParams } from "next/navigation"
import { useLocale } from "next-intl"

import { getPathname, useRouter } from "@/i18n/navigation"
import { DEFAULT_REGION } from "@/lib/region"
import { localizeHref, pathWithQuery, ROUTES } from "@/lib/routes"
import { useNavigationPath } from "@/platform/app-mode/NavigationProvider"

const useCountryCode = () => useParams<{ countryCode?: string }>().countryCode ?? DEFAULT_REGION

export const useLocalizedHref = () => {
  const locale = useLocale()
  const countryCode = useCountryCode()
  const navigationPath = useNavigationPath()
  return useCallback(
    (path: string) => navigationPath(getPathname({ locale, href: localizeHref(path.startsWith("/") ? path : `/${path}`, countryCode) })),
    [locale, countryCode, navigationPath]
  )
}

export const useSearchAction = () => useLocalizedHref()(`/${ROUTES.SEARCH}`)

const useRouterPath = () => {
  const countryCode = useCountryCode()
  const navigationPath = useNavigationPath()
  return useCallback((path: string) => navigationPath(localizeHref(path.startsWith("/") ? path : `/${path}`, countryCode)), [countryCode, navigationPath])
}

const useLocalizedNavigation = (method: "push" | "replace") => {
  const router = useRouter()
  const routerPath = useRouterPath()
  return useCallback((path: string, options?: { scroll?: boolean }) => router[method](routerPath(path), options), [router, routerPath, method])
}

export const useLocalizedPush = () => useLocalizedNavigation("push")
export const useLocalizedReplace = () => useLocalizedNavigation("replace")

export const useLocalizedPrefetch = () => {
  const router = useRouter()
  const routerPath = useRouterPath()
  return useCallback((path: string) => router.prefetch(routerPath(path)), [router, routerPath])
}

/**
 * Query updates that only client components read (sheet open/close flags).
 * Native history keeps the server route untouched — router.push would re-render
 * the page's server tree, which reads as a full reload behind the sheet.
 */
export const useShallowQueryParams = () => {
  return useCallback((updates: Record<string, string | number | boolean | null | undefined>, options?: { replace?: boolean }) => {
    const params = new URLSearchParams(window.location.search)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        params.delete(key)
      } else {
        params.set(key, String(value))
      }
    })
    const queryString = params.toString()
    const url = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
    if (options?.replace) {
      window.history.replaceState(null, "", url)
    } else {
      window.history.pushState(null, "", url)
    }
  }, [])
}

export const useQueryParams = () => {
  const router = useBrowserRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  return useCallback(
    (updates: Record<string, string | number | boolean | null | undefined>, options?: { replace?: boolean }) => {
      const params = new URLSearchParams(searchParams.toString())

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          params.delete(key)
        } else {
          params.set(key, String(value))
        }
      })

      const newUrl = pathWithQuery(pathname, params.toString())

      if (options?.replace) {
        router.replace(newUrl, { scroll: false })
      } else {
        router.push(newUrl, { scroll: false })
      }
    },
    [pathname, router, searchParams]
  )
}
